import { useSet } from '@mantine/hooks';
import React, { createContext, ReactNode, useState } from 'react';
import { MaterialType } from '../../../../../../backend/src/types.generated';
import { useFetchTrackableAssetsLazyQuery } from './graphql.generated';

// Define the shape of your context state
export interface BaleCreateContextValue {
	status: BaleCreateStatus;
	page: BaleCreatePageType;
	setPage: React.Dispatch<React.SetStateAction<BaleCreatePageType>>;
	handleScan: (code: string) => Promise<void>;
	loading: boolean;
	scanError: BaleScanError | null;
	setError: React.Dispatch<React.SetStateAction<BaleScanError | null>>;
	currentBale: CurrentBaleObject | null;
	setCurrentBale: React.Dispatch<React.SetStateAction<CurrentBaleObject | null>>;
	resetWorkflow: () => void;
	photo: string | null;
	setPhoto: React.Dispatch<React.SetStateAction<string | null>>;
}

export type BaleCreatePageType = 'SCAN' | 'MANUAL' | 'IDENTIFY' | 'PHOTO' | 'CONFIRM' | 'SUCCESS';

export type BaleCreateStatus =
	| 'PENDING'
	| 'VERIFYING'
	| 'CONFIRMED'
	| 'DUPLICATE'
	| 'ERROR'
	| 'INVALID'
	| null;

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const BaleCreateContext = createContext<BaleCreateContextValue | undefined>(undefined);

type BaleScanError = 'TAG_ALREADY_IN_USE' | 'ALREADY_SCANNED' | 'CODE_NOT_FOUND' | 'UNKNOWN_ERROR';

// Provider component props
interface BaleCreateContextProviderProps {
	children: ReactNode;
}

type CurrentBaleObject = {
	id: string;
	code: string;
	material: MaterialType | null;
	bags: number | null;
};

// Provider component
export const BaleCreateContextProvider: React.FC<BaleCreateContextProviderProps> = ({
	children,
}) => {
	const [page, setPage] = useState<BaleCreatePageType>('SCAN');
	const [status] = useState<BaleCreateStatus>(null);
	const [error, setError] = useState<BaleScanError | null>(null);
	const [fetchTrackableAssets, { loading }] = useFetchTrackableAssetsLazyQuery();
	const [currentBale, setCurrentBale] = useState<CurrentBaleObject | null>(null);
	const [photo, setPhoto] = useState<string | null>(null);
	const ignoreCode = useSet();

	const resetWorkflow = () => {
		ignoreCode.clear();
		setCurrentBale(null);
		setError(null);
		setPhoto(null);
		setPage('SCAN');
	};

	const handleScan = async (code: string) => {
		try {
			if (!ignoreCode.has(code)) {
				ignoreCode.add(code);
				const res = await fetchTrackableAssets({
					variables: {
						filter: {
							scannableCode: code.split('/').pop(),
						},
					},
				});
				const asset = res.data?.trackableAssets?.[0] || null;
				if (asset) {
					if (asset.status === 'BALE_CREATED') {
						setError('ALREADY_SCANNED');
					} else if (asset.status) {
						setError('TAG_ALREADY_IN_USE');
					} else {
						setCurrentBale({
							id: asset.id,
							code,
							material: null,
							bags: null,
						});
						setPage('IDENTIFY');
					}
				} else {
					setError('CODE_NOT_FOUND');
				}
			}
		} catch {
			setError('UNKNOWN_ERROR');
		}
	};

	return (
		<BaleCreateContext.Provider
			value={{
				status,
				page,
				setPage,
				handleScan,
				loading,
				scanError: error,
				setError,
				currentBale,
				setCurrentBale,
				resetWorkflow,
				photo,
				setPhoto,
			}}
		>
			{children}
		</BaleCreateContext.Provider>
	);
};
